import {
  knowledgeApiClient,
  Category,
  Article,
  PaginatedResponse,
  SearchResults,
  RagResponse,
  RagFeedback,
} from './api';

// Safely extract an array from an API response that may be
// a plain array or a wrapped object like { data: [...] }
function extractArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as { data: unknown }).data)) {
    return (data as { data: T[] }).data;
  }
  return [];
}

export const knowledgeService = {
  // Categories
  async getCategories(): Promise<Category[]> {
    const response = await knowledgeApiClient.get('/categories/public/tree');
    return extractArray<Category>(response.data);
  },

  async getCategoryBySlug(slug: string): Promise<Category> {
    const response = await knowledgeApiClient.get<Category>(`/categories/slug/${slug}`);
    return response.data;
  },

  async getCategoryBreadcrumb(categoryId: string): Promise<Category[]> {
    const response = await knowledgeApiClient.get(`/categories/${categoryId}/breadcrumb`);
    return extractArray<Category>(response.data);
  },

  // Articles
  async getPublishedArticles(options?: {
    categoryId?: string;
    tags?: string[];
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Article>> {
    const params = new URLSearchParams();
    if (options?.categoryId) params.append('categoryId', options.categoryId);
    if (options?.tags) options.tags.forEach(tag => params.append('tags', tag));
    if (options?.search) params.append('search', options.search);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await knowledgeApiClient.get<PaginatedResponse<Article>>(
      `/articles/published?${params.toString()}`
    );
    return response.data;
  },

  async getFeaturedArticles(limit?: number): Promise<Article[]> {
    const response = await knowledgeApiClient.get(
      `/articles/featured${limit ? `?limit=${limit}` : ''}`
    );
    return extractArray<Article>(response.data);
  },

  async getArticleBySlug(slug: string): Promise<Article> {
    const response = await knowledgeApiClient.get<Article>(`/articles/slug/${slug}`);
    return response.data;
  },

  async getRelatedArticles(articleId: string, limit?: number): Promise<Article[]> {
    const response = await knowledgeApiClient.get(
      `/articles/${articleId}/related${limit ? `?limit=${limit}` : ''}`
    );
    return extractArray<Article>(response.data);
  },

  async recordArticleView(articleId: string): Promise<void> {
    await knowledgeApiClient.post(`/articles/${articleId}/view`);
  },

  async submitArticleFeedback(
    articleId: string,
    isHelpful: boolean,
    comment?: string
  ): Promise<void> {
    await knowledgeApiClient.post(`/articles/${articleId}/feedback`, {
      isHelpful,
      comment,
    });
  },

  // Search
  async search(
    query: string,
    options?: {
      type?: 'all' | 'articles' | 'categories';
      categoryId?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<SearchResults> {
    const params = new URLSearchParams({ query });
    if (options?.type) params.append('type', options.type);
    if (options?.categoryId) params.append('categoryId', options.categoryId);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await knowledgeApiClient.get<SearchResults>(
      `/search?${params.toString()}`
    );
    return response.data;
  },

  async getSuggestions(query: string, limit?: number): Promise<string[]> {
    const response = await knowledgeApiClient.get(
      `/search/suggestions?query=${encodeURIComponent(query)}${limit ? `&limit=${limit}` : ''}`
    );
    return extractArray<string>(response.data);
  },

  async getPopularSearches(): Promise<{ queries: string[]; period: string }> {
    const response = await knowledgeApiClient.get('/search/popular');
    const data = response.data;
    // Backend returns { queries: string[], period: string }
    // BFF may return Array<{ query: string, count: number }> — handle both shapes
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.queries)) {
      return data as { queries: string[]; period: string };
    }
    if (Array.isArray(data)) {
      return { queries: data.map((item: { query: string }) => item.query), period: '30d' };
    }
    return { queries: [], period: '30d' };
  },

  // RAG Methods
  async ragQuery(
    query: string,
    options?: {
      maxSources?: number;
      threshold?: number;
      categoryIds?: string[];
    }
  ): Promise<RagResponse> {
    const response = await knowledgeApiClient.post<RagResponse>('/rag/query', {
      query,
      visibility: ['public'],
      sourceTypes: ['article'],
      maxSources: options?.maxSources || 5,
      threshold: options?.threshold || 0.3,
      categoryIds: options?.categoryIds,
      includeCitations: true,
    });
    return response.data;
  },

  async submitRagFeedback(feedback: RagFeedback): Promise<void> {
    await knowledgeApiClient.post('/rag/feedback', feedback);
  },
};
